import React, { Fragment } from 'react';
import renderIf from 'render-if';
import styles from '../Dashboards.module.css';
import classNames from 'classnames';
import MultiPanel from '../../common/MultiPanel';
import SectionedList from '../../common/SectionedList/SectionedList';
import {
  analyticsTrackEvent,
  VIEW_EXISTING_REPORTS,
  VIEW_EXTENSION_PANELS,
  IMAGES_BY_REPORT_TYPE,
  sectionedListValuesForFilterableTypes,
  contextToName,
  showNewOption,
} from '../helpers';
import {
  DASHBOARD_PANEL_CONTEXT_NOTE,
  DASHBOARD_PANEL_CONTEXT_WHITEBOARD,
} from '../constants';

function PanelLibrary({
  addRichTextPanel,
  selectedReport,
  setSelectedReport,
  selectedReportType,
  setSelectedReportType,
  setCurrentView,
  examplePanels,
  showExtensions,
  context,
}) {
  if (selectedReportType !== 'all') {
    examplePanels = examplePanels.filter(ex =>
      [ex.type, ex.example_type].flat().includes(selectedReportType)
    );
  }

  const tiles = examplePanels.map(details => {
    return {
      ...IMAGES_BY_REPORT_TYPE[details.type],
      title: details.name,
      description: details.description,
      id: details.path,
      record_type: details.path, // Needed for analytics tracking
      newPanel: showNewOption(details.type),
    };
  });
  const customPanelInfo = [
    {
      ...IMAGES_BY_REPORT_TYPE.existing,
      title: 'Use existing report',
      description: `Create a ${contextToName(
        context
      )} using a saved chart, list, pivot, or roadmap`,
      id: 'existing',
      navigationIcon: 'fa-angle-right',
    },
    addRichTextPanel && {
      ...IMAGES_BY_REPORT_TYPE.section_header,
      title: 'Text',
      description: `Share important details using rich text, links, and images`,
      id: 'rich-text',
      navigationIcon: 'fa-angle-right',
    },
  ].filter(x => x);

  const extensionPanelInfo = [
    {
      ...IMAGES_BY_REPORT_TYPE.existing,
      title: 'Add extension panel',
      description: `Create a ${contextToName(context)} using an extension`,
      id: 'extension',
      navigationIcon: 'fa-angle-right',
    },
  ];
  const selectedReportId = selectedReport?.id;
  const filterValues = [{ text: 'All', value: 'all' }].concat(
    sectionedListValuesForFilterableTypes(context).map(type => {
      type.new = showNewOption(type.value);
      return type;
    })
  );

  return (
    <Fragment>
      <div className={styles.ModalBodyTitle}>
        {context === DASHBOARD_PANEL_CONTEXT_NOTE ||
        context === DASHBOARD_PANEL_CONTEXT_WHITEBOARD
          ? 'View library'
          : 'Panel library'}
      </div>
      <div className={styles.ModalBodyContent}>
        <MultiPanel>
          <SectionedList
            options={[
              {
                title: '',
                value: '',
                key: 'report-view-type-selection',
                children: filterValues,
              },
            ]}
            selected={selectedReportType}
            onClick={item => setSelectedReportType(item.value)}
          />
          <div className={styles.PanelLibraryMain}>
            <div className={styles.PanelLibrarySection}>
              <div
                className={classNames(
                  'image-description-list-skinny-bubbles',
                  'image-description-list-multi-column',
                  'image-description-list--two-column'
                )}
              >
                <div className={styles.PanelLibrarySectionTitle}>
                  Custom {contextToName(context)}
                </div>

                <div className={'image-description-list'}>
                  {customPanelInfo.map(option => {
                    const {
                      image,
                      selectedImage,
                      title,
                      description,
                      id,
                      navigationIcon,
                      newPanel,
                    } = option;

                    return (
                      <aha-visual-selection
                        src={selectedImage}
                        not-selected-src={image}
                        key={id}
                        onClick={() => {
                          switch (option.id) {
                            case 'existing': {
                              analyticsTrackEvent(
                                'User selects use existing report'
                              );
                              setSelectedReport(null);
                              setCurrentView(VIEW_EXISTING_REPORTS);
                              break;
                            }
                            case 'rich-text': {
                              analyticsTrackEvent('User adds example panel', {
                                source: 'Dashboard',
                                panelType: 'Text',
                              });
                              setSelectedReport(null);
                              addRichTextPanel();
                              break;
                            }
                          }
                        }}
                        class={'image-description-list__item'}
                      >
                        {renderIf(newPanel)(
                          <span slot='more-info' className='new-icon ml-1' />
                        )}
                        <div
                          slot='title'
                          className='described-image__description-title'
                        >
                          <b>{title}</b>
                          {renderIf(newPanel)(
                            <span className='new-icon ml-1' />
                          )}
                        </div>
                        <p slot='description'>{description}</p>
                        <i
                          slot='icon'
                          className={classNames(
                            'fa-regular',
                            navigationIcon,
                            'nav-icon'
                          )}
                          aria-hidden='true'
                        />
                      </aha-visual-selection>
                    );
                  })}
                </div>
              </div>
            </div>
            {renderIf(showExtensions)(
              <div className={styles.PanelLibrarySection}>
                <div
                  className={classNames(
                    'image-description-list-skinny-bubbles',
                    styles.CustomPanelBubble
                  )}
                >
                  <div className={styles.PanelLibrarySectionTitle}>
                    Extension {contextToName(context)}
                  </div>

                  <div className={'image-description-list'}>
                    {extensionPanelInfo.map(option => {
                      const {
                        image,
                        selectedImage,
                        title,
                        description,
                        id,
                        navigationIcon,
                      } = option;

                      return (
                        <aha-visual-selection
                          src={selectedImage}
                          not-selected-src={image}
                          key={id}
                          onClick={() => {
                            analyticsTrackEvent(
                              'User selects use extension panel'
                            );
                            setSelectedReport(null);
                            setCurrentView(VIEW_EXTENSION_PANELS);
                          }}
                          class={'image-description-list__item'}
                        >
                          <div
                            slot='title'
                            className='described-image__description-title'
                          >
                            <b>{title}</b>
                          </div>
                          <p slot='description'>{description}</p>
                          <i
                            slot='icon'
                            className={classNames(
                              'fa-regular',
                              navigationIcon,
                              'nav-icon'
                            )}
                            aria-hidden='true'
                          />
                        </aha-visual-selection>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {renderIf(tiles.length > 0)(
              <div className={styles.PanelLibrarySection}>
                <div className={styles.PanelLibrarySectionTitle}>
                  Example {contextToName(context, true)}
                </div>
                <div className='image-description-list-multi-column image-description-list--two-column image-description-list-bubbles'>
                  <div className={'image-description-list'}>
                    {tiles.map(report => {
                      const {
                        image,
                        selectedImage,
                        title,
                        description,
                        id,
                        newPanel,
                      } = report;
                      const isSelected = id === selectedReportId;

                      return (
                        <aha-visual-selection
                          src={selectedImage}
                          not-selected-src={image}
                          selected={isSelected ? '' : undefined}
                          key={id}
                          onClick={() => {
                            if (report.id === selectedReportId) {
                              setSelectedReport(null);
                            } else {
                              setSelectedReport(report);
                            }
                          }}
                          class={'image-description-list__item'}
                        >
                          <div
                            slot='title'
                            className='described-image__description-title'
                          >
                            <b>{title}</b>
                            {renderIf(newPanel)(
                              <span className='new-icon ml-1' />
                            )}
                          </div>
                          <p slot='description'>{description}</p>
                        </aha-visual-selection>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </MultiPanel>
      </div>
    </Fragment>
  );
}

export default PanelLibrary;
